import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Sparkles, MapPin, X, Gem, Crown, Tag } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useNavigate } from "react-router-dom";

const MobileQuickActions = ({ onSkinAnalysis, onFaceMap }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenSeen, setHasBeenSeen] = useState(false);
  // Mirror of the desktop floater rotation (Jun 22, 2026): the last
  // tile in the fan-out flips between "1-Minute Skin Tip" and
  // "Promotions" every 9s. Driven by a single index so the dot
  // indicator stays in sync with the label + route + icon.
  const [tipFace, setTipFace] = useState(0); // 0 = skin tip, 1 = promotions

  useEffect(() => {
    const timer = setTimeout(() => setHasBeenSeen(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setTipFace((i) => (i + 1) % 2), 9000);
    return () => clearInterval(id);
  }, []);

  const tipPromoAction = tipFace === 0
    ? {
        id: "aesthetic-guide",
        icon: Sparkles,
        label: language === "fr" ? "Conseil du Jour" : "Karine's 1 Minute Skin Tip",
        sublabel: new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }),
        bgStyle: "bg-gradient-to-br from-[#8b5a2b] via-[#b87333] to-[#8b5a2b]",
        shadow: "shadow-[0_4px_15px_rgba(139,90,43,0.5)]",
        onClick: () => { setIsOpen(false); navigate("/aesthetic-guide"); window.scrollTo(0, 0); },
        useImage: true,
        imageSrc: "https://customer-assets.emergentagent.com/job_8776705b-3965-48ce-aa62-68a5febfba36/artifacts/5jnl5i2k_Untitled%20%28Website%29%20%281%29.png",
      }
    : {
        id: "promotions",
        icon: Tag,
        label: language === "fr" ? "Promotions de la Semaine" : "This Week's Promotions",
        sublabel: language === "fr" ? "Offres Crown · Touchez pour voir →" : "Crown Deals · Tap to view →",
        bgStyle: "bg-gradient-to-br from-[#1a0d05] via-[#3d2914] to-[#1a0d05]",
        shadow: "shadow-[0_4px_18px_rgba(196,164,82,0.5)]",
        textColor: "text-[#dfc06e]",
        onClick: () => { setIsOpen(false); navigate("/promotions"); window.scrollTo(0, 0); },
      };

  const actions = [
    {
      id: "crown-reserve",
      icon: Crown,
      label: language === "fr" ? "Crown Reserve" : "Crown Reserve Points & Plans",
      bgStyle: "bg-gradient-to-br from-[#c9a227] via-[#d4af37] to-[#c9a227]",
      shadow: "shadow-[0_4px_15px_rgba(180,130,70,0.5)]",
      onClick: () => { setIsOpen(false); navigate("/crown-reserve-hub"); window.scrollTo(0, 0); },
    },
    {
      id: "skin-analysis",
      icon: Camera,
      label: language === "fr" ? "Analyse Photo IA" : "AI Photo Analysis",
      bgStyle: "bg-gradient-to-br from-[#4a3728] via-[#5d4332] to-[#4a3728]",
      shadow: "shadow-[0_4px_15px_rgba(74,55,40,0.6)]",
      textColor: "text-[#d4af37]",
      onClick: () => { setIsOpen(false); onSkinAnalysis(); },
    },
    tipPromoAction,
  ];

  return (
    <div className="md:hidden fixed bottom-[7rem] right-4 z-40 kl-scroll-fade" data-testid="mobile-quick-actions">
      {/* Backdrop when open */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-[2px] z-40"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Action buttons that fan out */}
      <AnimatePresence>
        {isOpen && (
          <div className="absolute bottom-16 right-0 z-50 flex flex-col-reverse items-end gap-3">
            {actions.map((action, index) => (
              <motion.button
                key={action.id}
                initial={{ opacity: 0, y: 20, scale: 0.6 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.6 }}
                transition={{
                  delay: index * 0.07,
                  type: "spring",
                  stiffness: 400,
                  damping: 22,
                }}
                onClick={action.onClick}
                className="flex items-center gap-2.5 group"
                data-testid={`quick-action-${action.id}`}
              >
                {/* Label pill */}
                <motion.span
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.07 + 0.1 }}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full bg-gradient-to-r from-[#3d2914] to-[#4a3728] text-[#d4af37] border border-[#d4af37]/40 whitespace-nowrap shadow-lg"
                  style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "0.02em" }}
                >
                  <span>{action.label}</span>
                  {action.sublabel && (
                    <span className="block text-[9px] text-[#d4af37]/70 font-normal mt-0.5">
                      {action.sublabel}
                    </span>
                  )}
                  {/* Rotation indicator dots — only on the slot that
                      flips between Skin Tip and Promotions. Lets users
                      know another tap will show the other face. */}
                  {(action.id === "aesthetic-guide" || action.id === "promotions") && (
                    <span className="flex items-center gap-1 mt-1.5 justify-end" aria-hidden>
                      <span
                        className="w-1.5 h-1.5 rounded-full transition-all duration-500"
                        style={{ background: tipFace === 0 ? "#d4af37" : "rgba(212,175,55,0.3)" }}
                      />
                      <span
                        className="w-1.5 h-1.5 rounded-full transition-all duration-500"
                        style={{ background: tipFace === 1 ? "#d4af37" : "rgba(212,175,55,0.3)" }}
                      />
                    </span>
                  )}
                </motion.span>
                {/* Icon circle */}
                {action.useImage ? (
                  <div className={`w-12 h-12 rounded-full overflow-hidden ${action.shadow} transition-transform group-active:scale-90 border-2 border-[#d4af37]/50`}>
                    <img src={action.imageSrc} alt={action.label} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div
                    className={`w-12 h-12 rounded-full ${action.bgStyle} ${action.shadow} flex items-center justify-center ${action.textColor || 'text-white'} transition-transform group-active:scale-90 border-2 border-[#d4af37]/50`}
                  >
                    <action.icon className="w-5 h-5" />
                  </div>
                )}
              </motion.button>
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Main floating trigger button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="relative z-50 w-12 h-12 rounded-full flex items-center justify-center text-white transition-all"
        style={{
          background: isOpen
            ? "linear-gradient(135deg, #3f3f46 0%, #27272a 100%)"
            : "linear-gradient(135deg, #92700C 0%, #C9A84C 40%, #D4B25A 60%, #A07D2E 100%)",
          boxShadow: isOpen
            ? "0 4px 16px rgba(0,0,0,0.3)"
            : "0 4px 20px rgba(146,112,12,0.45), 0 0 0 2px rgba(212,178,90,0.15)",
        }}
        whileTap={{ scale: 0.9 }}
        data-testid="quick-actions-trigger"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <X className="w-5 h-5" />
            </motion.div>
          ) : (
            <motion.div
              key="gem"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Gem className="w-5 h-5" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Subtle attention pulse — only once, stops after first view */}
        {!hasBeenSeen && !isOpen && (
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{ border: "2px solid rgba(212,178,90,0.5)" }}
            animate={{ scale: [1, 1.6, 1.6], opacity: [0.7, 0, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
          />
        )}
      </motion.button>
    </div>
  );
};

export default MobileQuickActions;
